import React from 'react';
import PropTypes from 'prop-types';

import Page from '../Page';
import PaginationHeader from '../PaginationHeader';
import Pagination, { getPagingInfo } from '../Pagination';
import CreateButton from './CreateButton';
import pageSizeUtil from '../../util/pageSize';

/** adds paging around the provided content
 *  also renders a Page around all of the content unless fullPage is false
 *  ALL PROPS ARE PASSED TO PAGE (see Page documentation) */
export default class PagedView extends React.Component {
  static propTypes = {
    /** overrides the default behavior for activePage */
    activePage: PropTypes.number,
    /** children are rendered between the pagination header and pagination */
    children: PropTypes.any,
    /** the data set that paging is based off of */
    data: PropTypes.array.isRequired,
    /** if true, renders everthing within a Page component */
    fullPage: PropTypes.bool,
    /** shows loading indicator (should be set to true when data is fetching) */
    isLoadingData: PropTypes.bool,
    /** function should be used to fetch data */
    loadData: PropTypes.func.isRequired,
    /** max number of page buttons to show in with pagination
     *  (passed to Pagination components) */
    maxButtons: PropTypes.number,
    /** message that is shown when data is empty */
    noRecordsMessage: PropTypes.string,
    /** callback(activePage) whenever page is changed */
    onPageChange: PropTypes.func,
    /** overrides the default number of items expected on each page
     *  (passed to Pagination components) */
    pageSize: PropTypes.number,
    /** optional content that will be rendered immediately
     *  after the view's content */
    postContent: PropTypes.any,
    /** optional content that will be rendered immediately
     *  before the view's content */
    preContent: PropTypes.any,
    /** total number of data records available
     *  (passed to Pagination components) */
    totalCount: PropTypes.number
  }

  static defaultProps = {
    fullPage: true,
    loadData: () => {},
    resultsDisplayed: true
  }

  constructor(props) {
    super(props);

    this.state = {
      activePage: props.activePage || 1
    };
  }

  componentDidMount() {
    this.props.loadData();
  }

  componentWillReceiveProps(newProps) {
    const { activePage } = newProps;

    if(!!activePage && activePage !== this.state.activePage) {
      this.setState({ activePage });
    }
  }

  changePageSize = (pageSize) => {
    pageSizeUtil(pageSize);
    this.onPageChange(1);
  }

  onPageChange = (pageNumber) => {
    const { loadData, onPageChange } = this.props;
    if (onPageChange) {
      onPageChange(pageNumber);
    }
    this.setState({ activePage: pageNumber }, loadData);
    window.scrollTo(0, 0);
  }

  renderCard = (child, className) => {
    if (!child) {
      return null;
    }
    return (
      <div>
        <div className={`card-${className}`}>
          {child}
        </div>
        <div className="clearfix" />
      </div>
    )
  }

  wrapContent = (children) => {
    const { fullPage } = this.props;

    if (fullPage) {
      return (
        <Page
          pageType="parent"
          actionButton={<CreateButton {...this.props} />}
          {...this.props}>
          {children}
        </Page>
      );
    } else {
      return children;
    }
  }

  renderBodyContent = () => {
    const { preContent, postContent } = this.props;

    return (
      <React.Fragment>
        <div className="clearfix"/>
        {this.renderCard(preContent, 'pre')}
        {this.renderPagingContent()}
        {this.renderCard(postContent, 'post')}
      </React.Fragment>
    );
  }

  renderPagingContent = () => {
    const pagingInfo = getPagingInfo(this.props);
    const { pageSize } = this.props;
    const { activePage } = this.state;

    return (
      <React.Fragment>
        <PaginationHeader
          {...this.props}
          activePage={activePage}
          hasPagination={pagingInfo.hasPagination}
          showPageSizeDropdown={!pageSize}
          pSize={pagingInfo.pageSize}
          changePageSize={this.changePageSize}
          dataSize={pagingInfo.dataSize}
        />
        {this.renderContent()}
        {this.renderPagination(pagingInfo)}
      </React.Fragment>
    );
  }

  renderPagination = (pagingInfo) => {
    const { data, maxButtons, pageSize, totalCount } = this.props;
    const { activePage } = this.state;

    if (!pagingInfo || !pagingInfo.hasPagination) {
      return null;
    }
    return (
      <Pagination
        data={data}
        activePage={activePage}
        maxButtons={maxButtons}
        pageSize={pageSize}
        totalCount={totalCount}
        onPageChange={this.onPageChange}
      />
    );
  }

  renderNoContent = () => {
    const { noRecordsMessage } = this.props;
    return (
      <div className="page-panel">
        {noRecordsMessage}
      </div>
    );
  }

  renderContent = () => {
    return this.props.children;
  }

  render() {
    const { data, isLoadingData, noRecordsMessage } = this.props;
    const content = noRecordsMessage &&
      !isLoadingData &&
      (data === undefined || !data.length) ?
      this.renderNoContent() :
      this.renderBodyContent();

    return this.wrapContent(content);
  }
}
